import { useMemo, useRef, useState } from 'react';
import {
  type CorrigoChecklist,
  type CorrigoProcedure,
  type CorrigoStep,
  type CorrigoStepPushResult,
  type CorrigoStepStatus,
  type CorrigoStepUpdateInput,
} from '@tcg/shared';
import { ApiClientError, corrigoApi } from '../lib/api';
import { useSession } from '../lib/session';
import { Badge, Banner, Button, Card, CardBody, CardHeader, Field, Input, PageHeader, Spinner } from '../components/ui';
import { IconChecklist } from '../components/icons';

interface Edit {
  status: CorrigoStepStatus;
  value: string;
  comment: string;
}

type Edits = Record<number, Edit>;

function initEdits(checklist: CorrigoChecklist): Edits {
  const edits: Edits = {};
  for (const proc of checklist.procedures) {
    for (const step of proc.steps) {
      edits[step.id] = {
        status: step.status,
        value: step.value ?? '',
        comment: step.comment ?? '',
      };
    }
  }
  return edits;
}

function isChanged(step: CorrigoStep, edit: Edit | undefined): boolean {
  if (!edit) return false;
  return (
    edit.status !== step.status ||
    edit.value !== (step.value ?? '') ||
    edit.comment !== (step.comment ?? '')
  );
}

function buildUpdate(step: CorrigoStep, edit: Edit): CorrigoStepUpdateInput {
  const update: CorrigoStepUpdateInput = { id: step.id, status: edit.status };
  if (edit.value !== (step.value ?? '')) update.value = edit.value;
  if (edit.comment !== (step.comment ?? '')) update.comment = edit.comment;
  return update;
}

const localKey = (workOrderId: number) => `corrigo-checklist:${workOrderId}`;

function loadLocalEdits(workOrderId: number): Edits | null {
  try {
    const raw = localStorage.getItem(localKey(workOrderId));
    return raw ? (JSON.parse(raw) as Edits) : null;
  } catch {
    return null;
  }
}

export function CorrigoChecklist() {
  const { user } = useSession();
  const isAdmin = Boolean(user?.isAdmin);

  const [number, setNumber] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);

  const [checklist, setChecklist] = useState<CorrigoChecklist | null>(null);
  const [edits, setEdits] = useState<Edits>({});
  const [pushResults, setPushResults] = useState<Map<number, CorrigoStepPushResult> | null>(null);
  const [pushSummary, setPushSummary] = useState<string | null>(null);
  const [savedNote, setSavedNote] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const stepsById = useMemo(() => {
    const map = new Map<number, CorrigoStep>();
    if (checklist) for (const p of checklist.procedures) for (const s of p.steps) map.set(s.id, s);
    return map;
  }, [checklist]);

  const changedIds = useMemo(() => {
    const ids: number[] = [];
    stepsById.forEach((step, id) => {
      if (isChanged(step, edits[id])) ids.push(id);
    });
    return ids;
  }, [stepsById, edits]);

  const stepCount = checklist?.procedures.reduce((n, p) => n + p.steps.length, 0) ?? 0;
  const assetNames = useMemo(
    () =>
      Array.from(
        new Set((checklist?.procedures ?? []).map((p) => p.assetName).filter(Boolean) as string[]),
      ),
    [checklist],
  );

  const applyChecklist = (next: CorrigoChecklist) => {
    const base = initEdits(next);
    const local = loadLocalEdits(next.workOrderId);
    if (local) {
      for (const id of Object.keys(local)) {
        const nid = Number(id);
        if (base[nid]) base[nid] = { ...base[nid], ...local[nid] };
      }
      setSavedNote('Restored your locally saved edits.');
    }
    setChecklist(next);
    setEdits(base);
  };

  const pull = async () => {
    if (!number.trim()) return;
    setBusy(true);
    setError(null);
    setNotFound(false);
    setPushResults(null);
    setPushSummary(null);
    setChecklist(null);
    try {
      const search = await corrigoApi.search(number.trim(), {
        from: from || undefined,
        to: to || undefined,
      });
      if (search.matches.length === 0) {
        setNotFound(true);
        return;
      }
      const checklistData = await corrigoApi.getChecklist(search.matches[0].workOrderId);
      applyChecklist(checklistData);
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Could not pull the work order.');
    } finally {
      setBusy(false);
    }
  };

  const setStepEdit = (id: number, patch: Partial<Edit>) => {
    setEdits((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));
  };

  /** Mark every step in a procedure done (or not) — backs the "Mark all done" toggle. */
  const markAllInProcedure = (proc: CorrigoProcedure, done: boolean) => {
    setEdits((prev) => {
      const next = { ...prev };
      for (const step of proc.steps) {
        const base = next[step.id] ?? {
          status: 'Open' as CorrigoStepStatus,
          value: step.value ?? '',
          comment: step.comment ?? '',
        };
        next[step.id] = { ...base, status: done ? 'Done' : 'Open' };
      }
      return next;
    });
  };

  const saveLocally = () => {
    if (!checklist) return;
    try {
      localStorage.setItem(localKey(checklist.workOrderId), JSON.stringify(edits));
      setSavedNote(`Saved ${changedIds.length} edit(s) locally on this device.`);
    } catch {
      setError('Could not save locally (storage unavailable).');
    }
  };

  const importPdf = async (file: File) => {
    if (!checklist) return;
    setImporting(true);
    setError(null);
    setPushSummary(null);
    try {
      const result = await corrigoApi.importVendorPdf(checklist.workOrderId, file);
      setEdits((prev) => {
        const next = { ...prev };
        for (const r of result.responses) {
          const base = next[r.stepId] ?? { status: 'Open' as CorrigoStepStatus, value: '', comment: '' };
          next[r.stepId] = {
            status: r.status ?? base.status,
            value: r.value ?? base.value,
            comment: r.comment ?? base.comment,
          };
        }
        return next;
      });
      const parts = [`Imported ${result.responses.length} response(s) from the PDF.`];
      if (result.unmatchedStepIds.length) {
        parts.push(`${result.unmatchedStepIds.length} step(s) in the PDF did not match this work order.`);
      }
      setSavedNote(parts.join(' '));
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Could not import the PDF.');
    } finally {
      setImporting(false);
    }
  };

  const doPush = async () => {
    if (!checklist) return;
    const steps = changedIds
      .map((id) => {
        const step = stepsById.get(id);
        const edit = edits[id];
        return step && edit ? buildUpdate(step, edit) : null;
      })
      .filter((s): s is CorrigoStepUpdateInput => s !== null);

    if (steps.length === 0) {
      setConfirmOpen(false);
      return;
    }

    setPushing(true);
    setError(null);
    try {
      const result = await corrigoApi.pushSteps(checklist.workOrderId, {
        workOrderNumber: checklist.workOrderNumber,
        steps,
      });
      const byId = new Map(result.results.map((r) => [r.id, r]));
      const okCount = result.results.filter((r) => r.ok).length;
      // Re-fetched checklist confirms the new Corrigo state.
      try {
        localStorage.removeItem(localKey(checklist.workOrderId));
      } catch {
        /* ignore */
      }
      setChecklist(result.checklist);
      setEdits(initEdits(result.checklist));
      setPushResults(byId);
      setPushSummary(`${okCount}/${result.results.length} step(s) confirmed updated in Corrigo.`);
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Push failed.');
    } finally {
      setPushing(false);
      setConfirmOpen(false);
    }
  };

  const canPush = Boolean(checklist?.canPush) && isAdmin;

  return (
    <div className="space-y-6">
      <PageHeader
        backTo="/"
        icon={<IconChecklist className="h-5 w-5" />}
        title="Corrigo Checklist"
        subtitle="Pull a CorrigoPro work order, tick off each step, add comments where needed, and push the responses back to Corrigo."
      />

      {/* 1. Search panel */}
      <Card>
        <CardHeader title="Find a work order" subtitle="Enter the customer-visible work order number." />
        <CardBody className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="Work order number">
              <Input
                value={number}
                onChange={(e) => setNumber(e.target.value)}
                placeholder="US600LD000267"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void pull();
                }}
              />
            </Field>
            <Field label="Date from (optional)">
              <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            </Field>
            <Field label="Date to (optional)">
              <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
            </Field>
          </div>
          <div className="flex items-center gap-3">
            <Button onClick={() => void pull()} disabled={busy || !number.trim()}>
              {busy ? <Spinner /> : 'Pull Work Order'}
            </Button>
            <span className="text-xs text-slate-400">
              No dates? We scan the last 180 days by created date, then last-updated.
            </span>
          </div>
          {error && <Banner tone="error">{error}</Banner>}
          {notFound && (
            <Banner tone="warn">
              Work order <strong>{number}</strong> was not found. Try widening the date range.
            </Banner>
          )}
        </CardBody>
      </Card>

      {checklist && (
        <>
          {savedNote && <Banner tone="info">{savedNote}</Banner>}
          {pushSummary && <Banner tone="success">{pushSummary}</Banner>}

          {/* 2. Work order summary */}
          <SummaryCard checklist={checklist} stepCount={stepCount} assetNames={assetNames} />

          {!checklist.canPush && (
            <Banner tone="warn">
              {checklist.pushBlockedReason ??
                `This work order is read-only in Corrigo status: ${checklist.status}.`}{' '}
              You can still review and download the vendor PDF.
            </Banner>
          )}
          {checklist.canPush && !isAdmin && (
            <Banner tone="info">
              You can review and download the PDF. Pushing checklist responses to Corrigo requires an
              authorized (admin) user.
            </Banner>
          )}

          {/* 3. Procedure viewer with inline editing */}
          {checklist.procedures.map((proc) => {
            const allDone = proc.steps.length > 0 && proc.steps.every((s) => edits[s.id]?.status === 'Done');
            const someDone = proc.steps.some((s) => edits[s.id]?.status === 'Done');
            return (
              <Card key={proc.id}>
                <CardHeader
                  title={proc.name}
                  subtitle={
                    [proc.assetName && `Asset: ${proc.assetName}`, proc.status && `Status: ${proc.status}`]
                      .filter(Boolean)
                      .join(' · ') || undefined
                  }
                  action={
                    <div className="flex items-center gap-3">
                      {canPush && (
                        <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-600">
                          <input
                            type="checkbox"
                            className="h-4 w-4"
                            checked={allDone}
                            ref={(el) => {
                              if (el) el.indeterminate = !allDone && someDone;
                            }}
                            onChange={() => markAllInProcedure(proc, !allDone)}
                          />
                          Mark all done
                        </label>
                      )}
                      <Badge tone="slate">{proc.steps.length} steps</Badge>
                    </div>
                  }
                />
                <CardBody className="space-y-4">
                  {(proc.summary || proc.operationalRisks || proc.backoutPlan) && (
                    <dl className="grid gap-2 text-xs text-slate-500 sm:grid-cols-3">
                      {proc.summary && <Meta label="Summary" value={proc.summary} />}
                      {proc.operationalRisks && <Meta label="Operational risks" value={proc.operationalRisks} />}
                      {proc.backoutPlan && <Meta label="Backout plan" value={proc.backoutPlan} />}
                    </dl>
                  )}
                  {proc.steps.map((step) => (
                    <StepRow
                      key={step.id}
                      step={step}
                      edit={edits[step.id]}
                      changed={isChanged(step, edits[step.id])}
                      result={pushResults?.get(step.id)}
                      onEdit={(patch) => setStepEdit(step.id, patch)}
                      editable={canPush}
                    />
                  ))}
                </CardBody>
              </Card>
            );
          })}

          {/* Hidden input that backs the "Import Filled PDF" button. */}
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf,.pdf"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void importPdf(f);
              e.target.value = '';
            }}
          />

          {/* 4. Action bar */}
          <ActionBar
            checklist={checklist}
            canPush={canPush}
            changedCount={changedIds.length}
            pushing={pushing}
            importing={importing}
            onImportPdf={() => fileInputRef.current?.click()}
            onDownloadPdf={async () => {
              setError(null);
              try {
                await corrigoApi.downloadVendorPdf(
                  checklist.workOrderId,
                  `${checklist.workOrderNumber}_Checklist.pdf`,
                );
              } catch (err) {
                setError(err instanceof ApiClientError ? err.message : 'Could not download the PDF.');
              }
            }}
            onSaveLocally={saveLocally}
            onPush={() => setConfirmOpen(true)}
          />
        </>
      )}

      {confirmOpen && checklist && (
        <ConfirmModal
          workOrderNumber={checklist.workOrderNumber}
          count={changedIds.length}
          busy={pushing}
          onCancel={() => setConfirmOpen(false)}
          onConfirm={() => void doPush()}
        />
      )}
    </div>
  );
}

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="font-medium uppercase tracking-wide text-slate-400">{label}</dt>
      <dd className="mt-0.5 whitespace-pre-wrap text-slate-600">{value}</dd>
    </div>
  );
}

function SummaryCard({
  checklist,
  stepCount,
  assetNames,
}: {
  checklist: CorrigoChecklist;
  stepCount: number;
  assetNames: string[];
}) {
  const loc = checklist.serviceLocation;
  const locationText = loc
    ? [loc.occupantName, loc.address, loc.spaceName].filter(Boolean).join(' · ')
    : '—';
  return (
    <Card>
      <CardHeader
        title="Work order"
        action={<Badge tone={checklist.canPush ? 'green' : 'amber'}>{checklist.status}</Badge>}
      />
      <CardBody>
        <dl className="grid grid-cols-2 gap-4 text-sm sm:grid-cols-3">
          <Detail label="WO Number" value={checklist.workOrderNumber} />
          <Detail label="WO Id" value={String(checklist.workOrderId)} />
          <Detail label="Status" value={checklist.status} />
          <Detail label="Customer" value={checklist.customerName || '—'} />
          <Detail label="Location" value={locationText} />
          <Detail label="Assets" value={assetNames.length ? assetNames.join(', ') : '—'} />
          <Detail label="Procedures" value={String(checklist.procedures.length)} />
          <Detail label="Steps" value={String(stepCount)} />
        </dl>
      </CardBody>
    </Card>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-slate-400">{label}</dt>
      <dd className="mt-0.5 font-medium text-slate-800">{value}</dd>
    </div>
  );
}

function StepRow({
  step,
  edit,
  changed,
  result,
  onEdit,
  editable,
}: {
  step: CorrigoStep;
  edit: Edit | undefined;
  changed: boolean;
  result?: CorrigoStepPushResult;
  onEdit: (patch: Partial<Edit>) => void;
  editable: boolean;
}) {
  const e = edit ?? { status: step.status, value: step.value ?? '', comment: step.comment ?? '' };
  const done = e.status === 'Done';
  return (
    <div className="rounded-lg border border-slate-200 p-4">
      <div className="flex items-start justify-between gap-3">
        <label className="flex cursor-pointer items-start gap-3">
          <input
            type="checkbox"
            className="mt-1 h-4 w-4"
            checked={done}
            disabled={!editable}
            onChange={(ev) => onEdit({ status: ev.target.checked ? 'Done' : 'Open' })}
            aria-label={`Mark step ${step.stepNumber} done`}
          />
          <span className="font-medium text-slate-900">
            <span className="text-slate-400">{step.stepNumber}.</span> {step.title}
            {step.isRequired && <span className="ml-1 text-xs text-red-500">*</span>}
          </span>
        </label>
        <div className="flex shrink-0 items-center gap-2">
          {changed && <Badge tone="amber">Edited</Badge>}
          {result && <Badge tone={result.ok ? 'green' : 'red'}>{result.ok ? 'Pushed' : 'Failed'}</Badge>}
        </div>
      </div>

      {step.instructionsText && (
        <div className="mt-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Instructions</p>
          <p className="mt-1 whitespace-pre-wrap text-sm text-slate-700">{step.instructionsText}</p>
        </div>
      )}
      {step.notesText && (
        <div className="mt-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Notes</p>
          <p className="mt-1 whitespace-pre-wrap text-sm text-slate-600">{step.notesText}</p>
        </div>
      )}

      {result?.error && <p className="mt-2 text-xs text-red-600">{result.error}</p>}

      <div className="mt-3">
        <Field label="Comment (optional)">
          <textarea
            value={e.comment}
            disabled={!editable}
            onChange={(ev) => onEdit({ comment: ev.target.value })}
            rows={2}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-accent-500 focus:ring-2 focus:ring-accent-100 disabled:bg-slate-50 disabled:text-slate-400"
            placeholder="Add a comment"
          />
        </Field>
      </div>
    </div>
  );
}

function ActionBar({
  checklist,
  canPush,
  changedCount,
  pushing,
  importing,
  onImportPdf,
  onDownloadPdf,
  onSaveLocally,
  onPush,
}: {
  checklist: CorrigoChecklist;
  canPush: boolean;
  changedCount: number;
  pushing: boolean;
  importing: boolean;
  onImportPdf: () => void;
  onDownloadPdf: () => void;
  onSaveLocally: () => void;
  onPush: () => void;
}) {
  return (
    <div className="sticky bottom-0 z-10 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white/95 p-4 shadow-sm backdrop-blur">
      <div className="text-sm text-slate-500">
        {changedCount > 0 ? (
          <span>
            <strong className="text-slate-700">{changedCount}</strong> step(s) edited
          </span>
        ) : (
          <span>No local edits yet.</span>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="secondary" onClick={onDownloadPdf} aria-label={`Download ${checklist.workOrderNumber} fillable PDF`}>
          Download Fillable PDF
        </Button>
        <Button variant="secondary" onClick={onImportPdf} disabled={importing} aria-label="Import a filled checklist PDF">
          {importing ? <Spinner /> : 'Import Filled PDF'}
        </Button>
        <Button variant="secondary" onClick={onSaveLocally} disabled={changedCount === 0}>
          Save Locally
        </Button>
        <Button onClick={onPush} disabled={!canPush || pushing || changedCount === 0}>
          Push to Corrigo
        </Button>
      </div>
    </div>
  );
}

function ConfirmModal({
  workOrderNumber,
  count,
  busy,
  onCancel,
  onConfirm,
}: {
  workOrderNumber: string;
  count: number;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
      <Card className="w-full max-w-md">
        <CardHeader title="Push to Corrigo?" />
        <CardBody className="space-y-4">
          <p className="text-sm text-slate-600">
            This will update Corrigo procedure checklist steps for WO{' '}
            <strong>{workOrderNumber}</strong> ({count} step{count === 1 ? '' : 's'}). Continue?
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={onCancel} disabled={busy}>
              Cancel
            </Button>
            <Button onClick={onConfirm} disabled={busy || count === 0}>
              {busy ? <Spinner /> : 'Push to Corrigo'}
            </Button>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
